/**
 * AIWARI character creator — heraldic dark fantasy redesign (R232q5h+).
 *
 * Premium AAA private-server feel. Multi-stage flow:
 *   1. Faction  — три heraldic sigil cards (Серебряная Заря / Бурное
 *                Племя / Одичалые).
 *   2. Race     — карточки рас, отфильтрованные по фракции.
 *   3. Class    — карточки классов с описанием способностей.
 *   4. Customise — пол + телосложение. Live Babylon canvas виден за UI.
 *                  ⚠ skin tone / hair colour / hair style вырезаны
 *                  (R232q5b: Sirus rigs имеют baked textures —
 *                  setColours/setHairStyle на rig'е стояли как no-op
 *                  и слайдеры были display-only обманом).
 *   5. Name     — итоговое имя + summary card + большая золотая кнопка.
 *
 * Visual system reused 1:1 from packages/portal/src/pages/lobby.tsx:
 *   - Warm-near-black #0B0907 фон, antique gold #C9A96A акценты,
 *     parchment #E8DCC0 карточки, Cinzel + Spectral SC + EB Garamond +
 *     JetBrains Mono шрифты, SVG corner ornaments, filigree dividers,
 *     медленные взвешенные motion-кривые.
 *
 * Hard constraints:
 *   - export name `CharacterCreator` сохранён.
 *   - Логика sceneRig (Babylon RaceModelMap → Realistic GLB → KayKit →
 *     placeholder), useUI store integration, faction/race/class
 *     валидация, canConfirm контракт — нетронуты.
 *   - Никаких упоминаний внешних франшиз.
 */

import { Vector3 } from '@babylonjs/core';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CLASSES, type ClassDef, type ClassId } from '../data/classes.js';
import {
  FACTIONS,
  RACE_STATS,
  type Faction,
  racesByFaction,
  raceById,
  type RaceDef,
} from '../data/wow-races.js';
import { useUI } from '../state/store.js';
import { useScene } from '../scene/Engine.js';
import {
  createInvisibleCharacterAnchor,
  type PlaceholderCharacterRig,
} from '../scene/PlaceholderCharacter.js';
import { loadKayKitCharacter, type KayKitRig } from '../scene/KayKitCharacter.js';
import type { GlbCharacterRig } from '../scene/GlbCharacter.js';
import { LoadingScreen } from './LoadingScreen.js';
// R232q86g: getRacePortraitUrl no longer needed in customize-stage
// (replaced with live Babylon Sirus rig). May be re-added for race-card
// thumbnails on race-stage when 22 PNG portraits land.
// import { getRacePortraitUrl } from '../data/race-portraits.js';

// ─── PALETTE / TYPOGRAPHY ─────────────────────────────────────────────
// Reused 1:1 from portal/lobby.tsx so картина креатора и порталов
// читается как один шильдик одного бренда.
const COLOR = {
  bg:        '#0B0907',
  bgSoft:    '#15110D',
  parchment: '#E8DCC0',
  goldHi:    '#F2D58A',
  gold:      '#C9A96A',
  goldDark:  '#8A6B2E',
  goldDeep:  '#5C4520',
  ink:       '#2A201A',
  inkSoft:   '#3D2F26',
  textDim:   '#A89876',
  alliance:  '#5E8FD9', // Орден — холодное серебряно-синее
  horde:     '#C0533D', // Племя — тёплое медно-красное
  outcast:   '#8C7039', // Одичалые — потускневшая бронза
} as const;

const FONT = {
  display:  "'Cinzel', 'Times New Roman', serif",
  caps:     "'Spectral SC', 'Cinzel', serif",
  body:     "'EB Garamond', 'Georgia', serif",
  mono:     "'JetBrains Mono', 'Courier New', monospace",
} as const;

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?' +
  'family=Cinzel:wght@500;700;900&' +
  'family=Spectral+SC:wght@400;500;700&' +
  'family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&' +
  'family=JetBrains+Mono:wght@400;500&display=swap';

function useAiwariFonts() {
  useEffect(() => {
    if (document.querySelector('link[data-aiwari-fonts]')) return;
    const pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';
    const pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    link.setAttribute('data-aiwari-fonts', '');
    document.head.append(pre1, pre2, link);
  }, []);
}

// ─── HERALDIC PRIMITIVES ─────────────────────────────────────────────
// SVG corner ornament — копия из lobby.tsx чтобы карточки и панели
// получили тот же манускриптный угловой росчерк.
function CornerOrnament({
  flipX = false,
  flipY = false,
  size = 64,
  style,
}: {
  flipX?: boolean;
  flipY?: boolean;
  size?: number;
  style?: React.CSSProperties;
}) {
  const transform = `${flipX ? 'scaleX(-1) ' : ''}${flipY ? 'scaleY(-1) ' : ''}`.trim();
  return (
    <svg
      viewBox="0 0 96 96"
      width={size}
      height={size}
      style={{ position: 'absolute', pointerEvents: 'none', transform: transform || undefined, ...style }}
      aria-hidden
    >
      <defs>
        <linearGradient id={`cornerGold-${size}-${flipX}-${flipY}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={COLOR.goldDark} />
          <stop offset="50%" stopColor={COLOR.goldHi} />
          <stop offset="100%" stopColor={COLOR.goldDark} />
        </linearGradient>
      </defs>
      <g
        fill="none"
        stroke={`url(#cornerGold-${size}-${flipX}-${flipY})`}
        strokeWidth={1.4}
        strokeLinecap="round"
      >
        <path d="M0 1.5 L72 1.5" />
        <path d="M1.5 0 L1.5 72" />
        <path d="M1.5 24 L24 24 L24 1.5" opacity="0.65" />
        <path d="M10 10 Q22 10 22 22 Q22 34 10 34" />
        <path d="M30 1.5 L30 8 Q30 14 24 14" opacity="0.85" />
        <path d="M1.5 30 L8 30 Q14 30 14 24" opacity="0.85" />
        <circle cx={6} cy={6} r={1.3} fill={COLOR.goldHi} stroke="none" />
        <circle cx={22} cy={22} r={1.1} fill={COLOR.gold} stroke="none" />
      </g>
    </svg>
  );
}

function FiligreeDivider({ icon = '⚜', label }: { icon?: string; label?: string }) {
  return (
    <div
      className="flex items-center justify-center my-6 select-none"
      aria-hidden
      style={{ width: '100%' }}
    >
      <span
        style={{
          height: 1,
          flex: 1,
          maxWidth: 240,
          background: `linear-gradient(90deg, transparent, ${COLOR.goldDark}, ${COLOR.gold})`,
        }}
      />
      <span
        className="mx-3"
        style={{
          color: COLOR.gold,
          fontSize: 16,
          fontFamily: FONT.display,
          letterSpacing: '0.3em',
        }}
      >
        {label ? (
          <>
            <span style={{ marginRight: 10 }}>{icon}</span>
            {label}
            <span style={{ marginLeft: 10 }}>{icon}</span>
          </>
        ) : (
          icon
        )}
      </span>
      <span
        style={{
          height: 1,
          flex: 1,
          maxWidth: 240,
          background: `linear-gradient(270deg, transparent, ${COLOR.goldDark}, ${COLOR.gold})`,
        }}
      />
    </div>
  );
}

// Карточка с углами в стиле манускрипта.
function HeraldicPanel({
  children,
  className,
  style,
  selected,
  accent = COLOR.gold,
  onClick,
  disabled,
  size = 'md',
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  accent?: string;
  onClick?: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ornament = size === 'sm' ? 28 : size === 'lg' ? 72 : 44;
  const interactive = Boolean(onClick) && !disabled;
  return (
    <motion.div
      onClick={interactive ? onClick : undefined}
      whileHover={
        interactive
          ? { y: -4, transition: { duration: 0.2, ease: [0.2, 0.6, 0.2, 1] } }
          : undefined
      }
      whileTap={interactive ? { y: -1, transition: { duration: 0.08 } } : undefined}
      className={className}
      style={{
        position: 'relative',
        background: selected
          ? `linear-gradient(180deg, rgba(232,220,192,0.12) 0%, rgba(11,9,7,0.95) 100%)`
          : `linear-gradient(180deg, rgba(21,17,13,0.92) 0%, rgba(11,9,7,0.96) 100%)`,
        border: `1px solid ${selected ? accent : COLOR.goldDeep}`,
        boxShadow: selected
          ? `0 0 0 1px ${accent}55 inset, 0 0 28px ${accent}33, 0 12px 28px rgba(0,0,0,0.55)`
          : `0 0 0 1px rgba(0,0,0,0.6) inset, 0 8px 20px rgba(0,0,0,0.45)`,
        cursor: interactive ? 'pointer' : disabled ? 'not-allowed' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 220ms ease, box-shadow 220ms ease',
        ...style,
      }}
    >
      <CornerOrnament size={ornament} style={{ top: -1, left: -1 }} />
      <CornerOrnament size={ornament} style={{ top: -1, right: -1 }} flipX />
      <CornerOrnament size={ornament} style={{ bottom: -1, left: -1 }} flipY />
      <CornerOrnament size={ornament} style={{ bottom: -1, right: -1 }} flipX flipY />
      {children}
    </motion.div>
  );
}

// Подзаголовок-eyebrow в духе манускрипта.
function Eyebrow({ children, color = COLOR.gold }: { children: React.ReactNode; color?: string }) {
  return (
    <div
      style={{
        fontFamily: FONT.caps,
        fontWeight: 500,
        fontSize: 11,
        letterSpacing: '0.45em',
        textTransform: 'uppercase',
        color,
      }}
    >
      {children}
    </div>
  );
}

function DisplayHeading({ children, size = 36 }: { children: React.ReactNode; size?: number }) {
  return (
    <h2
      style={{
        fontFamily: FONT.display,
        fontWeight: 700,
        fontSize: size,
        lineHeight: 1.1,
        margin: 0,
        background: `linear-gradient(180deg, ${COLOR.goldHi} 0%, ${COLOR.gold} 50%, ${COLOR.goldDark} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '0 1px 0 rgba(0,0,0,0.5)',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </h2>
  );
}

function BodyItalic({
  children,
  color = COLOR.parchment,
  size = 15,
}: {
  children: React.ReactNode;
  color?: string;
  size?: number;
}) {
  return (
    <p
      style={{
        fontFamily: FONT.body,
        fontStyle: 'italic',
        fontSize: size,
        lineHeight: 1.5,
        color,
        margin: 0,
        opacity: 0.92,
      }}
    >
      {children}
    </p>
  );
}

// Большая кнопка — золотое тиснёное поле.
function GoldButton({
  children,
  onClick,
  disabled,
  size = 'md',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const pad = size === 'sm' ? '10px 18px' : size === 'lg' ? '18px 32px' : '14px 24px';
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 17 : 14;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? undefined : { y: -2, transition: { duration: 0.18 } }}
      whileTap={disabled ? undefined : { y: 0, transition: { duration: 0.08 } }}
      style={{
        padding: pad,
        fontSize,
        fontFamily: FONT.display,
        fontWeight: 700,
        letterSpacing: '0.32em',
        textTransform: 'uppercase',
        color: disabled ? '#6b5a3f' : COLOR.ink,
        background: disabled
          ? `linear-gradient(180deg, #2a1f15 0%, #15110d 100%)`
          : `linear-gradient(180deg, ${COLOR.goldHi} 0%, ${COLOR.gold} 50%, ${COLOR.goldDark} 100%)`,
        border: `1px solid ${disabled ? COLOR.goldDeep : COLOR.goldDark}`,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled
          ? 'inset 0 0 8px rgba(0,0,0,0.6)'
          : `0 0 0 1px rgba(0,0,0,0.45) inset, 0 1px 0 rgba(255,255,255,0.35) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 0 24px ${COLOR.goldHi}33`,
        textShadow: disabled ? 'none' : '0 1px 0 rgba(255,255,255,0.35)',
        transition: 'box-shadow 220ms ease',
      }}
    >
      {children}
    </motion.button>
  );
}

function GhostButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 18px',
        fontFamily: FONT.caps,
        fontSize: 11,
        letterSpacing: '0.4em',
        textTransform: 'uppercase',
        color: disabled ? COLOR.goldDeep : COLOR.gold,
        background: 'transparent',
        border: `1px solid ${disabled ? COLOR.goldDeep : COLOR.goldDark}`,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'color 200ms ease, border-color 200ms ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.borderColor = COLOR.goldHi;
          e.currentTarget.style.color = COLOR.goldHi;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.borderColor = COLOR.goldDark;
          e.currentTarget.style.color = COLOR.gold;
        }
      }}
    >
      {children}
    </button>
  );
}

// ─── BRANDED FACTION OVERLAY ──────────────────────────────────────────
// Канон бренда AIWARI: 3 сигила (Серебряная Заря / Бурное Племя /
// Одичалые). Data-модель пока поддерживает только 2 фракции
// (alliance / horde), поэтому третья отображается как "Одичалые —
// скоро". Маппинг хранится здесь, чтобы НЕ ломать схему CharacterDraft.
type BrandFactionKey = 'silver-dawn' | 'storm-tribe' | 'unnamed';

interface BrandedFaction {
  key: BrandFactionKey;
  faction: Faction | null; // null = locked-soon
  name: string;
  motto: string;
  blurb: string;
  sigil: string;
  accent: string;
  stripe: string;
  locked?: boolean;
}

const BRANDED_FACTIONS: BrandedFaction[] = [
  {
    key: 'silver-dawn',
    faction: 'alliance',
    name: 'Орден Серебряной Зари',
    motto: 'Свет. Закон. Долг.',
    blurb:
      'Союз людей, дворфов, ночных эльфов и гномов. Хранители древних замков и серебряных дорог — стоят за порядок и память предков.',
    sigil: 'brand/faction_silver_dawn.jpg',
    accent: COLOR.alliance,
    stripe: '#7AA4DE',
  },
  {
    key: 'storm-tribe',
    faction: 'horde',
    name: 'Бурное Племя',
    motto: 'Кровь. Земля. Свобода.',
    blurb:
      'Орки, тролли, тауренский народ и нежить, отвергшая Лича. Свободные кочевники под чёрно-красными знамёнами шторма.',
    sigil: 'brand/faction_storm_tribe.jpg',
    accent: COLOR.horde,
    stripe: '#E27B65',
  },
  {
    key: 'unnamed',
    faction: 'outcast',
    name: 'Одичалые',
    motto: 'Ни знамён. Ни имён.',
    blurb:
      'Изгои, которых не приняли ни Орден, ни Племя. Третья сила Стормвейла. Гоблины, полукровки и теневой народ.',
    sigil: 'brand/faction_unnamed.jpg',
    accent: COLOR.outcast,
    stripe: '#B69054',
    locked: false,
  },
];

// Per-race emoji glyph для портретного медальона (race-themed
// силуэт). Лучше первой буквы — та читалась как калькулятор.
const RACE_GLYPH: Record<string, string> = {
  human:       '⚔',
  dwarf:       '🛡',
  'night-elf': '🏹',
  gnome:       '⚙',
  orc:         '🪓',
  undead:      '☠',
  tauren:      '🐂',
  troll:       '🗡',
  // Outcast — Одичалые (R232q5w)
  goblin:      '⚒',
  'half-blood':'⚜',
  shadowkin:   '✦',
};

const CLASS_GLYPH: Record<ClassId, string> = {
  warrior: '⚔',
  paladin: '⚜',
  ranger:  '🏹',
  rogue:   '🗡',
  cleric:  '✟',
  deathknight: '💀',
  shaman:  '⚡',
  mage:    '✦',
  warlock: '☄',
  druid:   '❦',
};

// R232q20+: Sirus 3.3.5a-style unified flow. The final 'customize' stage
// now also hosts the name input + summary + "Создать героя" CTA — same
// screen as in WotLK 3.3.5a character creator. The old standalone 'name'
// stage was removed.
type Stage = 'faction' | 'race' | 'class' | 'customize';
const STAGE_ORDER: Stage[] = ['faction', 'race', 'class', 'customize'];
const STAGE_LABEL: Record<Stage, string> = {
  faction:   'Сторона',
  race:      'Народ',
  class:     'Стезя',
  customize: 'Облик и Имя',
};

// Имена-подсказки для финального шага.
const SUGGESTED_NAMES: Record<Faction, string[]> = {
  alliance: ['Эльдан Серебряный', 'Бранвина', 'Тордил Кремень'],
  horde:    ['Грокмар Кровавый', 'Сайла Темнокрыл', 'Драг’зул Громовой'],
  outcast:  ['Зокс Сребромонет', 'Вильда Полукровка', 'Тенесвет Сумрак'],
};

// Валидация имени: 3-20 символов, кириллица или латиница, пробелы и
// апостроф разрешены, остальное — нет. Простая локальная фильтр-полоса
// против явной грубости.
const NAME_RX = /^[A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё '\-]{2,19}$/;
const PROFANITY_BLOCK = ['admin', 'moderator', 'gm', 'хуй', 'пизд', 'ебан', 'fuck', 'shit'];

function isNameOk(name: string): boolean {
  const v = name.trim();
  if (!NAME_RX.test(v)) return false;
  const lower = v.toLowerCase();
  return !PROFANITY_BLOCK.some((bad) => lower.includes(bad));
}

// R232q5b: HAIR_STYLES, Swatch, NumberOption, ColorChip удалены вместе с
// fake skin/hair/style контролами. Если когда-нибудь rig обзаведётся
// runtime colour swap, восстанавливать из git history.

// Ниже — основной компонент.
export function CharacterCreator() {
  useAiwariFonts();
  const reduce = useReducedMotion();
  const setStage = useUI((s) => s.setStage);
  const setDraft = useUI((s) => s.setDraft);
  const draft = useUI((s) => s.draft);
  const { scene } = useScene();
  const rigRef = useRef<PlaceholderCharacterRig | null>(null);
  const kayRigRef = useRef<KayKitRig | null>(null);
  // R232q75: пока Sirus rig едет — LoadingScreen overlay поверх canvas.
  // Если все ступени pickRig упали — error state со словесной ошибкой
  // и Retry-кнопкой. Никакого procedural placeholder больше не рендерится.
  const [rigLoading, setRigLoading] = useState(true);
  const [rigError, setRigError] = useState<string | null>(null);
  const [rigEpoch, setRigEpoch] = useState(0);

  const [stage, setLocalStage] = useState<Stage>(() =>
    !draft.faction
      ? 'faction'
      : !draft.raceId
      ? 'race'
      : !draft.classId
      ? 'class'
      : 'customize',
  );

  // Если в localStorage висит огрызок предыдущей черновой записи
  // без имени — сбрасываем, чтобы не путать юзера полузаполненной
  // карточкой.
  useEffect(() => {
    if (!draft.name && (draft.faction || draft.raceId || draft.classId)) {
      setDraft({ faction: null, raceId: null, classId: null });
      setLocalStage('faction');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const race: RaceDef | undefined = draft.raceId ? raceById(draft.raceId) : undefined;
  const cls: ClassDef | undefined = draft.classId
    ? CLASSES.find((c) => c.id === draft.classId)
    : undefined;
  const factionDef = draft.faction ? FACTIONS[draft.faction] : null;
  const accent = race?.colour ?? factionDef?.colour ?? COLOR.gold;
  const brandedFaction = useMemo(
    () => BRANDED_FACTIONS.find((b) => b.faction === draft.faction) ?? null,
    [draft.faction],
  );

  // Build the preview rig — re-build whenever class/race/gender changes.
  // Полная логика сохранена 1:1 — ничего не выпиливаем, иначе сцена
  // развалится.
  useEffect(() => {
    if (!scene) return;
    let cancelled = false;
    // R232q75: каждая смена расы/класса/пола → новая попытка загрузки rig'а.
    setRigLoading(true);
    setRigError(null);
    // R232q75: вместо procedural Roblox-человечка ставим невидимый anchor.
    // React-слой выше (CharacterCreator render) показывает LoadingScreen
    // overlay поверх Babylon canvas пока Sirus rig едет. Если Sirus rig
    // упал — overlay переходит в state error c retry-кнопкой, никаких
    // заглушек больше не рисуется.
    const placeholder = createInvisibleCharacterAnchor(scene);
    placeholder.root.rotation.y = Math.PI;
    rigRef.current = placeholder;
    type PreviewRig =
      | KayKitRig
      | GlbCharacterRig
      | {
          root: import('@babylonjs/core').TransformNode;
          setColours?: (...a: unknown[]) => void;
          setMoving: (m: boolean, r?: boolean) => void;
          update: (dt: number) => void;
          setEquipment: (e: Record<string, string>) => void;
          setCosmetics?: (e: { cape: string | null; pet: string | null }) => void;
          triggerAttack?: () => void;
          dispose: () => void;
        };
    const loaderPromise: Promise<PreviewRig> = draft.raceId
      ? (Promise.all([
          import('../scene/RaceModelMap.js'),
          import('../scene/RealisticCharacter.js'),
        ]).then(async ([rmm, rc]) => {
          const _race = draft.raceId as import('../scene/RaceModelMap.js').Race;
          const _gender = (draft.gender ?? 'male') as import('../scene/RaceModelMap.js').Gender;
          const pick = rmm.getRaceModelGlb(_race, _gender);
          // R232q5b0: iterate Sirus-only chain (procedural sentinel + content-type guard).
          for (const step of pick.chain) {
            try {
              if (step.glb === rmm.AIWARI_CHAR_PLACEHOLDER) {
                return await rc.loadRealisticCharacter(scene, {
                  glb: step.glb,
                  baseDir: step.baseDir,
                  scale: pick.scale,
                  targetTallest: pick.targetHeight,
                  raceId: _race,
                  gender: _gender,
                  skinVariant: draft.skinVariant,
                  faceStyle: draft.faceStyle,
                });
              }
              const res = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
              if (!res.ok) continue;
              const ct = (res.headers.get('content-type') ?? '').toLowerCase();
              if (ct.includes('text/html')) continue;
              return await rc.loadRealisticCharacter(scene, {
                glb: step.glb,
                baseDir: step.baseDir,
                scale: pick.scale,
                targetTallest: pick.targetHeight,
                raceId: _race,
                gender: _gender,
                skinVariant: draft.skinVariant,
                faceStyle: draft.faceStyle,
              });
            } catch (e) {
              console.error('[CharacterCreator] rig chain step failed', step, e);
            }
          }
          return loadKayKitCharacter(scene, {
            classId: draft.classId ?? 'warrior',
            raceId: draft.raceId ?? undefined,
            gender: draft.gender,
          });
        }) as Promise<PreviewRig>)
      : loadKayKitCharacter(scene, {
          classId: draft.classId ?? 'warrior',
          raceId: draft.raceId ?? undefined,
          gender: draft.gender,
        });
    void loaderPromise
      .then((rig) => {
        if (cancelled) {
          rig.dispose();
          return;
        }
        placeholder.dispose();
        rig.root.rotation.y = 0;
        const bodyScale =
          draft.bodyType === 'slim'
            ? { x: 0.92, y: 1.0, z: 0.92 }
            : draft.bodyType === 'broad'
            ? { x: 1.12, y: 1.0, z: 1.12 }
            : { x: 1.0, y: 1.0, z: 1.0 };
        // Multiply onto the auto-normalized scale set by loadRealisticCharacter,
        // not replace it — otherwise the bbox normalization is discarded.
        const baseScale = rig.root.scaling.x;
        rig.root.scaling = new Vector3(
          baseScale * bodyScale.x,
          baseScale * bodyScale.y,
          baseScale * bodyScale.z,
        );
        kayRigRef.current = rig as KayKitRig;
        const starter =
          draft.classId === 'ranger'
            ? { mainhand: 'oak-bow' }
            : draft.classId === 'mage' || draft.classId === 'warlock'
            ? { mainhand: 'apprentice-staff' }
            : draft.classId === 'cleric' || draft.classId === 'shaman'
            ? { mainhand: 'apprentice-staff', offhand: 'wooden-shield' }
            : draft.classId === 'rogue'
            ? { mainhand: 'apprentice-dagger', offhand: 'apprentice-dagger-oh' }
            : draft.classId === 'druid'
            ? { mainhand: 'oak-bow' }
            : { mainhand: 'iron-sword', offhand: 'wooden-shield' };
        rig.setEquipment(starter as Record<string, string>);
        // R232q5b: skin/hair colour + hair style ранее звались на rig
        // здесь, но Sirus rigs имеют baked PBR textures — setColours
        // на них noop, setHairStyle вообще не существует. UI-слайдеры
        // были косметической обманкой. Сейчас эти поля убраны из
        // CustomizeStage и из NameStage summary, draft.skinTone/
        // hairColour/hairStyle всё ещё хранятся в store ради совместимости
        // со старыми персонажами на сервере, но геймплейно не работают.
        //
        // R232q53: реальный mesh-swap для skinVariant / faceStyle /
        // hairStyle / hairColourIdx / facialHair. setCustomization
        // существует только на Sirus rigs (RealisticRig), KayKit rigs
        // его не имеют — поэтому проверяем тип.
        const realistic = rig as { setCustomization?: (c: import('../scene/CharCustomization.js').CharCustomization) => Promise<void> };
        if (typeof realistic.setCustomization === 'function') {
          void realistic.setCustomization({
            skinVariant: draft.skinVariant,
            faceStyle: draft.faceStyle,
            hairStyle: draft.hairStyle,
            hairColourIdx: draft.hairColourIdx,
            facialHair: draft.facialHair,
          });
        }
        // R232q75: rig загружен → убираем LoadingScreen overlay.
        setRigLoading(false);
      })
      .catch((err: unknown) => {
        // R232q75: показываем error overlay с retry. Никаких placeholder
        // в кадре больше нет — анкор уже невидимый.
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[CharacterCreator] rig load failed', err);
        setRigError(msg || 'Не удалось загрузить модель героя');
        setRigLoading(false);
      });

    const obs = scene.onBeforeRenderObservable.add(() => {
      const dt = scene.getEngine().getDeltaTime() / 1000;
      const r = kayRigRef.current ?? rigRef.current;
      r?.update(dt);
    });
    return () => {
      cancelled = true;
      scene.onBeforeRenderObservable.remove(obs);
      kayRigRef.current?.dispose();
      kayRigRef.current = null;
      rigRef.current?.dispose();
      rigRef.current = null;
    };
  }, [scene, draft.gender, draft.bodyType, draft.classId, draft.raceId, rigEpoch]);

  // R232q5b: skinTone/hairColour/hairStyle effects были no-op (Sirus rigs
  // не поддерживают runtime colour swap). Эффекты удалены вместе с UI
  // слайдерами в CustomizeStage.

  // R232q53: re-apply customization on every slider change WITHOUT
  // reloading the rig. The setCustomization method is idempotent and
  // gated to 3 concurrent texture fetches inside, so a slider scrub
  // can't slam the server. Skip for KayKit fallback rigs (no method).
  useEffect(() => {
    const rig = kayRigRef.current as unknown as
      | { setCustomization?: (c: import('../scene/CharCustomization.js').CharCustomization) => Promise<void> }
      | null;
    if (!rig || typeof rig.setCustomization !== 'function') return;
    void rig.setCustomization({
      skinVariant: draft.skinVariant,
      faceStyle: draft.faceStyle,
      hairStyle: draft.hairStyle,
      hairColourIdx: draft.hairColourIdx,
      facialHair: draft.facialHair,
    });
  }, [
    draft.skinVariant,
    draft.faceStyle,
    draft.hairStyle,
    draft.hairColourIdx,
    draft.facialHair,
  ]);

  const allowedClasses = useMemo(() => {
    if (!race) return [] as typeof CLASSES;
    return CLASSES.filter((c) => race.classes.includes(c.id));
  }, [race]);

  const canConfirm =
    isNameOk(draft.name) && !!draft.classId && !!draft.raceId && !!draft.faction;

  const stageIndex = STAGE_ORDER.indexOf(stage);

  const goBack = () => {
    if (stage === 'customize') setLocalStage('class');
    else if (stage === 'class') setLocalStage('race');
    else if (stage === 'race') setLocalStage('faction');
    else setStage('character-roster');
  };

  const goForward = () => {
    if (stage === 'faction' && draft.faction) setLocalStage('race');
    else if (stage === 'race' && draft.raceId) setLocalStage('class');
    else if (stage === 'class' && draft.classId) setLocalStage('customize');
    // R232q20+: customize is the final stage. Submit happens via
    // NameStage's "Создать героя" CTA, not via «Дальше».
  };

  const submit = () => {
    if (!canConfirm) return;
    const legacySide = draft.faction === 'alliance' ? 'tidal' : 'anchorless';
    const subFactionFallback =
      draft.faction === 'alliance' ? 'house-saltcrest' : 'band-red-compass';
    setDraft({
      side: legacySide,
      subFactionId: draft.subFactionId ?? subFactionFallback,
    });
    setStage('world');
  };

  // R232q76: 3D-предпросмотр в креаторе временно отключён — Sirus rig
  // pipeline ещё кидает multi-parent / glb-sanitizer warnings и юзер
  // видит коричневый procedural-болванчик. Скрываем фиксированный
  // Babylon canvas (он живёт в BabylonEngine) на любом шаге креатора —
  // вместо него показываем 2D heraldic portrait. Логика rig-loader'а
  // выше остаётся жить, чтобы scene/sceneRig инвариант не сломался.
  useEffect(() => {
    const canvas = document.querySelector(
      'canvas',
    ) as HTMLCanvasElement | null;
    if (!canvas) return;
    const prevVisibility = canvas.style.visibility;
    const prevPointerEvents = canvas.style.pointerEvents;
    canvas.style.visibility = 'hidden';
    canvas.style.pointerEvents = 'none';
    return () => {
      canvas.style.visibility = prevVisibility;
      canvas.style.pointerEvents = prevPointerEvents;
    };
  }, []);

  // ─── RENDER ────────────────────────────────────────────────────────
  return (
    <div
      className="absolute inset-0 z-40"
      style={{
        fontFamily: FONT.body,
        color: COLOR.parchment,
        pointerEvents: 'auto',
      }}
    >
      {/* R232q76: 3D-предпросмотр отключён — никакого LoadingScreen overlay
          и rig-error модалки на customize стадии больше не показываем.
          Вместо них юзер видит чистый 2D heraldic portrait + sliders.
          Если rig pipeline стабилизируется — флаг ниже снова покажет
          оверлей. */}
      {false && stage === 'customize' && rigLoading && !rigError && (
        <LoadingScreen
          stage="Готовим героя"
          timeoutMs={30000}
          onTimeout={() => setRigError('Сервер не успел отдать модель героя за 30 секунд')}
          debugLabel={draft.raceId ? `${draft.raceId}/${draft.gender ?? 'male'}` : ''}
        />
      )}
      {false && stage === 'customize' && rigError && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
        >
          <div
            className="rounded-2xl p-8 max-w-md text-center"
            style={{
              background: 'linear-gradient(180deg, #1F1813, #0B0907)',
              border: '2px solid #8B2828',
              boxShadow: '0 0 40px rgba(139,40,40,0.4)',
              fontFamily: 'EB Garamond, serif',
            }}
          >
            <div
              className="text-[12px] tracking-[0.4em] uppercase mb-3"
              style={{ color: '#FAB300', fontFamily: "'Spectral SC', serif" }}
            >
              Не удалось вызвать героя
            </div>
            <p className="text-[#E8DCC0] text-[15px] leading-relaxed mb-5">{rigError}</p>
            <button
              type="button"
              onClick={() => {
                setRigError(null);
                setRigLoading(true);
                setRigEpoch((n) => n + 1);
              }}
              className="px-6 py-3 rounded-lg transition hover:scale-105"
              style={{
                background: 'linear-gradient(180deg, #FAB300, #B27F2F)',
                color: '#0F0900',
                fontFamily: 'Cinzel, serif',
                fontWeight: 700,
                letterSpacing: '0.06em',
              }}
            >
              Попробовать снова
            </button>
          </div>
        </div>
      )}
      {/* Backdrop — splash hero + ink wash. R232q86g: на customize-стадии
          backdrop-overlays отключены, чтобы реальный Babylon canvas с
          Sirus rig'ом был виден. На остальных стадиях (faction/race/class/
          name) backdrop остаётся как раньше. */}
      {stage !== 'customize' && (
        <>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${import.meta.env.BASE_URL}portraits/aiwari-creator-bg.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.32,
              filter: 'brightness(0.55) saturate(1.05)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 30%, rgba(0,0,0,0) 0%, ${COLOR.bg}cc 60%, ${COLOR.bg}f2 100%)`,
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'repeating-linear-gradient(0deg, rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.04) 1px, transparent 1px, transparent 3px)',
              opacity: 0.5,
              mixBlendMode: 'overlay',
            }}
          />
        </>
      )}
      {/* R232q86g: 2D portrait убран — на customize-стадии теперь виден
          реальный Babylon canvas с Sirus rig'ом (loadRealisticCharacter
          грузит /aiwari/models/sirus/Character/{Race}/{Gender}/...glb для
          выбранной race+gender). Все 22 GLB лежат на VPS, проверены
          HEAD-probe ранее. Loading screen показывается пока GLB парсится. */}
      {stage === 'customize' && rigLoading && !rigError && (
        <div
          className="absolute pointer-events-none"
          style={{
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            background: 'rgba(11,9,7,0.4)',
          }}
        >
          <div
            style={{
              padding: '14px 28px',
              fontFamily: FONT.caps,
              fontSize: 13,
              letterSpacing: '0.4em',
              color: COLOR.gold,
              border: `1px solid ${COLOR.goldDeep}`,
              background: 'rgba(11,9,7,0.85)',
            }}
          >
            ЗАГРУЗКА МОДЕЛИ {race?.name.ru ?? '...'}
          </div>
        </div>
      )}
      {stage === 'customize' && rigError && (
        <div
          className="absolute"
          style={{
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            background: 'rgba(11,9,7,0.6)',
            gap: 14,
          }}
        >
          <div style={{ fontFamily: FONT.body, fontSize: 14, color: '#E8B0A0' }}>
            Не удалось загрузить модель: {rigError}
          </div>
          <button
            onClick={() => setRigEpoch((x) => x + 1)}
            style={{
              padding: '8px 22px',
              border: `1px solid ${COLOR.gold}`,
              background: 'rgba(201,169,106,0.16)',
              color: COLOR.goldHi,
              fontFamily: FONT.caps,
              fontSize: 12,
              letterSpacing: '0.3em',
              cursor: 'pointer',
            }}
          >
            ПОВТОРИТЬ
          </button>
        </div>
      )}

      {/* ── HEADER — eyebrow brand + stage progress ──────────────── */}
      <header
        className="absolute top-0 left-0 right-0"
        style={{
          padding: '18px 28px 14px',
          background: `linear-gradient(180deg, rgba(11,9,7,0.92) 0%, rgba(11,9,7,0.55) 75%, transparent 100%)`,
          borderBottom: `1px solid ${COLOR.goldDeep}55`,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between"
          style={{ maxWidth: 1480 }}
        >
          <div className="flex items-center gap-3">
            <span
              style={{
                color: COLOR.gold,
                fontFamily: FONT.display,
                fontWeight: 900,
                fontSize: 18,
                letterSpacing: '0.4em',
              }}
            >
              WORLD OF VIBE CRAFT
            </span>
            <span
              style={{
                color: COLOR.goldDark,
                fontFamily: FONT.caps,
                fontSize: 11,
                letterSpacing: '0.35em',
                textTransform: 'uppercase',
                paddingLeft: 14,
                borderLeft: `1px solid ${COLOR.goldDeep}`,
              }}
            >
              Чертог Сотворения
            </span>
          </div>

          {/* Stage gemstones */}
          <div className="flex items-center" style={{ gap: 6 }}>
            {STAGE_ORDER.map((s, i) => {
              const isActive = s === stage;
              const isDone = i < stageIndex;
              return (
                <div key={s} className="flex items-center" style={{ gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Allow только обратную навигацию (на пройденные шаги).
                      if (isDone) setLocalStage(s);
                    }}
                    style={{
                      cursor: isDone ? 'pointer' : 'default',
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        transform: 'rotate(45deg)',
                        background: isActive
                          ? COLOR.goldHi
                          : isDone
                          ? COLOR.gold
                          : 'transparent',
                        border: `1px solid ${isActive || isDone ? COLOR.gold : COLOR.goldDeep}`,
                        boxShadow: isActive
                          ? `0 0 12px ${COLOR.goldHi}88`
                          : 'none',
                        transition: 'all 240ms ease',
                      }}
                    />
                    <span
                      style={{
                        fontFamily: FONT.caps,
                        fontSize: 10,
                        letterSpacing: '0.32em',
                        textTransform: 'uppercase',
                        color: isActive
                          ? COLOR.goldHi
                          : isDone
                          ? COLOR.gold
                          : COLOR.goldDeep,
                      }}
                    >
                      {STAGE_LABEL[s]}
                    </span>
                  </button>
                  {i < STAGE_ORDER.length - 1 && (
                    <span
                      style={{
                        width: 22,
                        height: 1,
                        background:
                          i < stageIndex ? COLOR.gold : COLOR.goldDeep,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <GhostButton onClick={goBack}>◂ Назад</GhostButton>
          </div>
        </div>
      </header>

      {/* ── BODY ──────────────────────────────────────────────────── */}
      <main
        className="absolute"
        style={{
          top: 84,
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: 'auto',
          padding: '32px 28px 48px',
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 1480 }}>
          <AnimatePresence mode="wait" initial={false}>
            {stage === 'faction' && (
              <motion.section
                key="faction"
                initial={reduce ? { opacity: 1 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 1 } : { opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.2, 0.6, 0.2, 1] }}
              >
                <FactionStage
                  selected={draft.faction}
                  onPick={(f) => {
                    if (!f) return;
                    setDraft({ faction: f, raceId: null, classId: null });
                    setLocalStage('race');
                  }}
                  reduce={!!reduce}
                />
              </motion.section>
            )}

            {stage === 'race' && (
              <motion.section
                key="race"
                initial={reduce ? { opacity: 1 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 1 } : { opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.2, 0.6, 0.2, 1] }}
              >
                <RaceStage
                  faction={draft.faction!}
                  brandedFaction={brandedFaction}
                  selected={draft.raceId}
                  onPick={(r) => {
                    setDraft({
                      raceId: r.id,
                      classId: r.classes.includes(draft.classId as never)
                        ? draft.classId
                        : null,
                      skinTone: r.skinTones[0]!,
                      hairColour: r.hairColours[0]!,
                    });
                    setLocalStage('class');
                  }}
                />
              </motion.section>
            )}

            {stage === 'class' && race && (
              <motion.section
                key="class"
                initial={reduce ? { opacity: 1 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 1 } : { opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.2, 0.6, 0.2, 1] }}
              >
                <ClassStage
                  race={race}
                  brandedFaction={brandedFaction}
                  classes={allowedClasses}
                  allClasses={CLASSES}
                  selected={draft.classId}
                  onPick={(c) => {
                    setDraft({ classId: c.id });
                    setLocalStage('customize');
                  }}
                />
              </motion.section>
            )}

            {stage === 'customize' && race && cls && brandedFaction && (
              <motion.section
                key="customize"
                initial={reduce ? { opacity: 1 } : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 1 } : { opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.2, 0.6, 0.2, 1] }}
              >
                {/* R232q20+: WotLK 3.3.5a-style unified customize-and-name
                    screen — gender + body + skin/face/hair sliders on the
                    right rail, name input + suggestions + final summary +
                    «Создать героя» CTA below the live 3D preview. */}
                <CustomizeStage
                  race={race}
                  cls={cls}
                  draft={draft}
                  setDraft={setDraft}
                  accent={accent}
                  onNext={submit}
                />
                {/* R232q86g: NameStage скрыт на customize шаге — он закрывал
                    весь центр экрана и пользователь не видел 3D rig.
                    Ввод имени и кнопка «Создать героя» доступны через
                    hoisted top-center блок (см. ниже). */}
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Bottom-right preview badge — over the live Babylon canvas. */}
      {stage === 'customize' && race && cls && (
        <HeraldicPanel
          size="sm"
          style={{
            position: 'absolute',
            bottom: 24,
            right: 24,
            padding: '10px 18px',
            display: 'none',
          }}
          className="md:!flex md:items-center md:gap-3"
        >
          <Eyebrow>Предпросмотр</Eyebrow>
          <span
            style={{
              fontFamily: FONT.display,
              fontWeight: 700,
              color: COLOR.goldHi,
              fontSize: 13,
              letterSpacing: '0.06em',
            }}
          >
            {race.name.ru} · {cls.name.ru}
          </span>
        </HeraldicPanel>
      )}

      {/* "Вперёд" в правом нижнем углу для шагов 1-3 (faction/race/class).
          На finalной customize-стадии submit идёт через NameStage CTA. */}
      {stage !== 'faction' && stage !== 'customize' && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 28,
            display: 'flex',
            gap: 10,
          }}
        >
          {((stage === 'race' && draft.raceId) ||
            (stage === 'class' && draft.classId)) && (
            <GoldButton size="sm" onClick={goForward}>
              Дальше ▸
            </GoldButton>
          )}
        </div>
      )}
      {/* R232q85: Hoisted name input + submit на customize-стадии. Раньше поле
          имени жило только в NameStage внизу (юзер не мог его найти при
          rubber-UI sizing на 4K экранах). Теперь поле + кнопка топ-центром,
          всегда видны над всем остальным. */}
      {stage === 'customize' && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(var(--aiwari-padding, 8px) * 1.5)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'linear-gradient(180deg, rgba(20,15,8,0.92) 0%, rgba(11,9,7,0.85) 100%)',
            border: `1px solid ${COLOR.goldDeep}`,
            borderRadius: 4,
            padding: '6px 10px',
            boxShadow: '0 4px 18px rgba(0,0,0,0.6)',
          }}
        >
          <span
            style={{
              fontFamily: FONT.caps,
              fontSize: 10,
              letterSpacing: '0.25em',
              color: COLOR.gold,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Имя:
          </span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ name: e.target.value.slice(0, 20) })}
            placeholder="введи имя героя"
            autoFocus
            style={{
              width: 220,
              padding: '6px 10px',
              fontFamily: FONT.display,
              fontSize: 14,
              fontWeight: 600,
              color: COLOR.goldHi,
              background: COLOR.bg,
              border: `1px solid ${
                draft.name.trim().length > 0
                  ? (isNameOk(draft.name) ? COLOR.gold : COLOR.horde)
                  : COLOR.goldDark
              }`,
              borderRadius: 2,
              outline: 'none',
              letterSpacing: '0.04em',
            }}
          />
          <GoldButton size="sm" onClick={submit} disabled={!canConfirm}>
            ⚜ Создать героя ⚜
          </GoldButton>
        </div>
      )}
    </div>
  );
}

// ─── STAGE 1: FACTION ─────────────────────────────────────────────────
function FactionStage({
  selected,
  onPick,
  reduce,
}: {
  selected: Faction | null;
  onPick: (f: Faction | null) => void;
  reduce: boolean;
}) {
  return (
    <div>
      <div className="text-center" style={{ marginBottom: 20 }}>
        <Eyebrow>Шаг 1 · Сторона</Eyebrow>
        <div style={{ height: 8 }} />
        <DisplayHeading size={48}>Выбери сторону</DisplayHeading>
        <div style={{ height: 12 }} />
        <BodyItalic color={COLOR.textDim} size={17}>
          За кого ты будешь сражаться в Стормвейле — за порядок Серебряной
          Зари или за свободу Бурного Племени?
        </BodyItalic>
      </div>

      <FiligreeDivider icon="⚜" label="Три знамени" />

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {BRANDED_FACTIONS.map((bf, i) => {
          const isSelected = bf.faction === selected;
          return (
            <motion.div
              key={bf.key}
              initial={reduce ? { opacity: 1 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: i * 0.06,
                ease: [0.2, 0.6, 0.2, 1],
              }}
            >
              <FactionCard
                bf={bf}
                selected={isSelected}
                onPick={() => onPick(bf.faction)}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function FactionCard({
  bf,
  selected,
  onPick,
}: {
  bf: BrandedFaction;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <HeraldicPanel
      selected={selected}
      accent={bf.accent}
      onClick={bf.locked ? undefined : onPick}
      disabled={bf.locked}
      size="lg"
      style={{
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top stripe — faction colour band. */}
      <div
        style={{
          height: 4,
          background: `linear-gradient(90deg, transparent, ${bf.stripe}, ${bf.accent}, ${bf.stripe}, transparent)`,
          opacity: selected ? 1 : 0.65,
        }}
      />

      {/* Sigil — large, breathing. */}
      <div
        style={{
          padding: '32px 24px 12px',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.018, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 168,
            height: 168,
            borderRadius: '50%',
            overflow: 'hidden',
            border: `2px solid ${selected ? bf.accent : COLOR.goldDeep}`,
            boxShadow: selected
              ? `0 0 0 1px ${bf.accent}55, 0 0 38px ${bf.accent}88, inset 0 0 24px rgba(0,0,0,0.55)`
              : `0 0 0 1px rgba(0,0,0,0.5), inset 0 0 24px rgba(0,0,0,0.55)`,
            background: `radial-gradient(circle at 50% 35%, ${bf.accent}33, transparent 70%)`,
            position: 'relative',
          }}
        >
          <img
            src={`${import.meta.env.BASE_URL}${bf.sigil}`}
            alt={bf.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: bf.locked ? 'grayscale(0.85) brightness(0.55)' : undefined,
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          {bf.locked && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLOR.goldHi,
                fontFamily: FONT.caps,
                fontSize: 11,
                letterSpacing: '0.4em',
                textTransform: 'uppercase',
                background: 'rgba(11,9,7,0.5)',
              }}
            >
              скоро
            </div>
          )}
        </motion.div>
      </div>

      <div
        style={{
          padding: '16px 24px 28px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flex: 1,
        }}
      >
        <Eyebrow color={bf.accent}>{bf.motto}</Eyebrow>
        <h3
          style={{
            fontFamily: FONT.display,
            fontWeight: 700,
            fontSize: 24,
            color: COLOR.goldHi,
            margin: 0,
            letterSpacing: '0.04em',
            textShadow: '0 1px 0 rgba(0,0,0,0.6)',
          }}
        >
          {bf.name}
        </h3>
        <BodyItalic size={14} color={COLOR.textDim}>
          {bf.blurb}
        </BodyItalic>
        {!bf.locked && bf.faction && (
          <>
            <div style={{ height: 6 }} />
            <div
              style={{
                paddingTop: 10,
                borderTop: `1px solid ${COLOR.goldDeep}66`,
                fontFamily: FONT.mono,
                fontSize: 11,
                color: bf.accent,
                letterSpacing: '0.04em',
              }}
            >
              {racesByFaction(bf.faction)
                .map((r) => r.name.ru)
                .join(' · ')}
            </div>
          </>
        )}
      </div>
    </HeraldicPanel>
  );
}

// ─── STAGE 2: RACE ────────────────────────────────────────────────────
function RaceStage({
  faction,
  brandedFaction,
  selected,
  onPick,
}: {
  faction: Faction;
  brandedFaction: BrandedFaction | null;
  selected: string | null;
  onPick: (r: RaceDef) => void;
}) {
  const races = racesByFaction(faction);
  return (
    <div>
      <div className="text-center" style={{ marginBottom: 20 }}>
        <Eyebrow color={brandedFaction?.accent ?? COLOR.gold}>
          Шаг 2 · {brandedFaction?.name ?? FACTIONS[faction].name.ru}
        </Eyebrow>
        <div style={{ height: 8 }} />
        <DisplayHeading size={44}>Выбери народ</DisplayHeading>
        <div style={{ height: 12 }} />
        <BodyItalic color={COLOR.textDim} size={16}>
          У каждого народа — свой облик, свой характер и своя боевая стезя.
          Выбирай по сердцу: землю, в которую хочется возвращаться.
        </BodyItalic>
      </div>

      <FiligreeDivider icon="❦" label="Народы" />

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 18,
          maxWidth: 1320,
          margin: '0 auto',
        }}
      >
        {races.map((r, i) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.32,
              delay: i * 0.06,
              ease: [0.2, 0.6, 0.2, 1],
            }}
          >
            <RaceCard
              race={r}
              selected={selected === r.id}
              onPick={() => onPick(r)}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function RaceCard({
  race,
  selected,
  onPick,
}: {
  race: RaceDef;
  selected: boolean;
  onPick: () => void;
}) {
  const stats = RACE_STATS[race.id];
  const statsRow: string[] = [];
  if (stats.hp) statsRow.push(`+${stats.hp} HP`);
  if (stats.pow) statsRow.push(`+${stats.pow} POW`);
  if (stats.armor) statsRow.push(`+${stats.armor} ARMOR`);
  if (stats.crit) statsRow.push(`+${stats.crit}% CRIT`);
  if (stats.haste) statsRow.push(`+${stats.haste}% HASTE`);
  if (stats.resist) statsRow.push(`+${stats.resist}% RES`);

  return (
    <HeraldicPanel
      selected={selected}
      accent={race.colour}
      onClick={onPick}
      size="md"
      style={{
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '20px 22px 14px',
          display: 'flex',
          gap: 16,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `radial-gradient(circle, ${race.colour}55 0%, ${race.colour}11 60%, transparent 80%)`,
            border: `1.5px solid ${selected ? race.colour : COLOR.goldDark}`,
            boxShadow: `inset 0 0 14px rgba(0,0,0,0.55), 0 0 ${selected ? 18 : 0}px ${race.colour}66`,
            fontSize: 38,
            color: COLOR.goldHi,
            textShadow: '0 2px 4px rgba(0,0,0,0.85)',
            flexShrink: 0,
          }}
        >
          {RACE_GLYPH[race.id] ?? race.name.ru[0]}
        </div>
        <div style={{ minWidth: 0 }}>
          <h3
            style={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 22,
              color: COLOR.goldHi,
              margin: 0,
              letterSpacing: '0.03em',
              textShadow: '0 1px 0 rgba(0,0,0,0.6)',
            }}
          >
            {race.name.ru}
          </h3>
          <Eyebrow color={race.colour}>{race.capital.ru}</Eyebrow>
        </div>
      </div>

      <div style={{ padding: '0 22px 14px' }}>
        <BodyItalic size={14} color={COLOR.textDim}>
          {race.blurb.ru}
        </BodyItalic>
      </div>

      {statsRow.length > 0 && (
        <div
          style={{
            margin: '0 22px',
            padding: '10px 0',
            borderTop: `1px solid ${COLOR.goldDeep}66`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {statsRow.map((s) => (
            <span
              key={s}
              style={{
                fontFamily: FONT.mono,
                fontSize: 11,
                color: COLOR.gold,
                background: 'rgba(0,0,0,0.35)',
                border: `1px solid ${COLOR.goldDeep}`,
                padding: '3px 8px',
                borderRadius: 2,
                letterSpacing: '0.05em',
              }}
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div style={{ flex: 1 }} />

      <div
        style={{
          padding: '10px 22px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          borderTop: `1px solid ${COLOR.goldDeep}40`,
        }}
      >
        {race.classes.map((cid) => {
          const c = CLASSES.find((x) => x.id === cid);
          if (!c) return null;
          return (
            <span
              key={cid}
              style={{
                fontFamily: FONT.caps,
                fontSize: 9,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: COLOR.gold,
                opacity: 0.85,
              }}
            >
              {c.name.ru}
            </span>
          );
        })}
      </div>
    </HeraldicPanel>
  );
}

// ─── STAGE 3: CLASS ───────────────────────────────────────────────────
function ClassStage({
  race,
  brandedFaction,
  classes,
  allClasses,
  selected,
  onPick,
}: {
  race: RaceDef;
  brandedFaction: BrandedFaction | null;
  classes: ClassDef[];
  allClasses: ClassDef[];
  selected: string | null;
  onPick: (c: ClassDef) => void;
}) {
  const restricted = allClasses.filter((c) => !race.classes.includes(c.id));
  return (
    <div>
      <div className="text-center" style={{ marginBottom: 20 }}>
        <Eyebrow color={brandedFaction?.accent ?? COLOR.gold}>
          Шаг 3 · {race.name.ru} · {race.capital.ru}
        </Eyebrow>
        <div style={{ height: 8 }} />
        <DisplayHeading size={44}>Выбери стезю</DisplayHeading>
        <div style={{ height: 12 }} />
        <BodyItalic color={COLOR.textDim} size={16}>
          Стезя — это путь, по которому ведёт тебя кровь и наставники. Сталь
          и ярость, святое слово или огонь, рождённый из тени.
        </BodyItalic>
      </div>

      <FiligreeDivider icon="✦" label={`${classes.length} стезей`} />

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 18,
          maxWidth: 1480,
          margin: '0 auto',
        }}
      >
        {classes.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.32,
              delay: i * 0.06,
              ease: [0.2, 0.6, 0.2, 1],
            }}
          >
            <ClassCard
              cls={c}
              selected={selected === c.id}
              onPick={() => onPick(c)}
            />
          </motion.div>
        ))}
      </div>

      {restricted.length > 0 && (
        <>
          <FiligreeDivider icon="✕" label="Закрыто для народа" />
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              maxWidth: 1480,
              margin: '0 auto',
              opacity: 0.55,
            }}
          >
            {restricted.map((c) => (
              <div
                key={c.id}
                style={{
                  border: `1px dashed ${COLOR.goldDeep}`,
                  padding: '10px 14px',
                  background: 'rgba(0,0,0,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 18,
                    color: COLOR.goldDeep,
                  }}
                >
                  {CLASS_GLYPH[c.id]}
                </span>
                <div>
                  <div
                    style={{
                      fontFamily: FONT.display,
                      fontWeight: 600,
                      fontSize: 14,
                      color: COLOR.gold,
                    }}
                  >
                    {c.name.ru}
                  </div>
                  <div
                    style={{
                      fontFamily: FONT.caps,
                      fontSize: 9,
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      color: COLOR.goldDeep,
                    }}
                  >
                    {race.name.ru} не идёт этим путём
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ClassCard({
  cls,
  selected,
  onPick,
}: {
  cls: ClassDef;
  selected: boolean;
  onPick: () => void;
}) {
  const signature = cls.abilities.slice(0, 4);
  const resourceLabel: Record<typeof cls.resource, string> = {
    mana:  'Мана',
    rage:  'Ярость',
    focus: 'Сосредоточение',
    faith: 'Вера',
    runic: 'Сила Рун',
  };
  return (
    <HeraldicPanel
      selected={selected}
      accent={cls.colour}
      onClick={onPick}
      size="md"
      style={{
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '20px 22px 14px',
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `radial-gradient(circle, ${cls.colour}44 0%, ${cls.colour}10 60%, transparent 80%)`,
            border: `1.5px solid ${selected ? cls.colour : COLOR.goldDark}`,
            boxShadow: `inset 0 0 12px rgba(0,0,0,0.6), 0 0 ${selected ? 14 : 0}px ${cls.colour}66`,
            fontSize: 32,
            color: COLOR.goldHi,
            flexShrink: 0,
          }}
        >
          {CLASS_GLYPH[cls.id]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 22,
              color: COLOR.goldHi,
              margin: 0,
              letterSpacing: '0.03em',
              textShadow: '0 1px 0 rgba(0,0,0,0.6)',
            }}
          >
            {cls.name.ru}
          </h3>
          <Eyebrow color={cls.colour}>{resourceLabel[cls.resource]}</Eyebrow>
        </div>
      </div>

      <div style={{ padding: '0 22px 12px' }}>
        <BodyItalic size={14} color={COLOR.textDim}>
          {cls.blurb.ru}
        </BodyItalic>
      </div>

      <div
        style={{
          margin: '0 22px',
          padding: '10px 0',
          borderTop: `1px solid ${COLOR.goldDeep}66`,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <StatPill label="HP" value={cls.baseStats.hp} />
        <StatPill label={resourceLabel[cls.resource].slice(0, 4).toUpperCase()} value={cls.baseStats.resource} />
        <StatPill label="ARM" value={cls.baseStats.armor} />
        <StatPill label="POW" value={cls.baseStats.pow} />
      </div>

      <div
        style={{
          padding: '12px 22px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderTop: `1px solid ${COLOR.goldDeep}40`,
        }}
      >
        <Eyebrow>Знаковые умения</Eyebrow>
        {signature.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              fontFamily: FONT.body,
              fontSize: 13,
              color: COLOR.parchment,
              opacity: 0.92,
            }}
          >
            <span style={{ color: cls.colour, fontSize: 14 }}>{a.icon}</span>
            <span style={{ fontWeight: 600 }}>{a.name.ru}</span>
            <span
              style={{
                fontFamily: FONT.mono,
                fontSize: 10,
                color: COLOR.goldDark,
                marginLeft: 'auto',
              }}
            >
              {a.cooldown ? `${a.cooldown}s` : 'instant'}
            </span>
          </div>
        ))}
      </div>
    </HeraldicPanel>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 4,
        fontFamily: FONT.mono,
        fontSize: 11,
        color: COLOR.parchment,
        background: 'rgba(0,0,0,0.4)',
        border: `1px solid ${COLOR.goldDeep}`,
        padding: '3px 8px',
        borderRadius: 2,
        letterSpacing: '0.05em',
      }}
    >
      <span style={{ color: COLOR.goldDark }}>{label}</span>
      <span style={{ color: COLOR.goldHi, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ─── STAGE 4: CUSTOMIZE ───────────────────────────────────────────────
// R232q5b: радикально упрощено. Раньше тут было 5 контролов: пол, тон
// кожи, причёска, цвет волос, телосложение. Из них реально влияли на
// 3D rig только пол (загружает другой GLB) и телосложение (root.scaling).
// Тон кожи / цвет волос / причёска вызывали setColours/setHairStyle на
// rig'е, который их игнорил (Sirus rigs имеют baked PBR textures).
// По требованию юзера — фейковые слайдеры выпилены.
/**
 * R232q20+: WotLK 3.3.5a-style customization counts.
 *
 * TODO(dbc): wire real per-race/per-gender counts from CharSections.dbc
 * (mounted read-only at /opt/aiwari-dbc/dbc/CharSections.dbc on VPS). For
 * now we expose hardcoded counts derived from Human-male as a baseline so
 * the UI is functional and persists choices to the player record. Visual
 * mesh swap is OUT OF SCOPE — placeholder rigs don't support runtime
 * texture/morph swap, but the indexes are saved for the future when
 * KayKit/Quaternius modular faces ship.
 */
const CUSTOM_COUNTS_DEFAULT = {
  skin: 5,
  face: 9,
  hair: 13,
  hairColour: 10,
  facialHair: 7,
};
const FEMALE_FACIAL_HAIR = 0;

function CustomizeStage({
  race,
  cls,
  draft,
  setDraft,
  accent,
  onNext,
}: {
  race: RaceDef;
  cls: ClassDef;
  draft: ReturnType<typeof useUI.getState>['draft'];
  setDraft: ReturnType<typeof useUI.getState>['setDraft'];
  accent: string;
  onNext: () => void;
}) {
  void onNext; // submit lives on the inline NameStage below this section
  // R232q20+: counts per axis. Female rigs in WotLK 3.3.5a had no facial-hair
  // axis — collapse the picker to "none" instead of hiding it abruptly.
  const counts = {
    skin: CUSTOM_COUNTS_DEFAULT.skin,
    face: CUSTOM_COUNTS_DEFAULT.face,
    hair: CUSTOM_COUNTS_DEFAULT.hair,
    hairColour: CUSTOM_COUNTS_DEFAULT.hairColour,
    facialHair: draft.gender === 'female' ? FEMALE_FACIAL_HAIR : CUSTOM_COUNTS_DEFAULT.facialHair,
  };
  return (
    <div>
      <div className="text-center" style={{ marginBottom: 14 }}>
        <Eyebrow>Шаг 4 · Облик и Имя</Eyebrow>
        <div style={{ height: 8 }} />
        <DisplayHeading size={36}>
          {race.name.ru} · {cls.name.ru}
        </DisplayHeading>
        <div style={{ height: 6 }} />
        <BodyItalic color={COLOR.textDim} size={14}>
          Подбери пол, кожу, лицо и причёску — справа. Имя запиши в Книгу
          Зала ниже. Модель за окном обновится сразу.
        </BodyItalic>
      </div>

      {/* Side panel — на правом крае экрана, чтобы 3D rig был хорошо
          виден слева/в центре. Минимум контролов, максимум сцены. */}
      <div
        style={{
          position: 'fixed',
          top: 110,
          right: 28,
          width: 360,
          maxWidth: 'calc(100vw - 56px)',
          maxHeight: 'calc(100vh - 140px)',
          overflowY: 'auto',
          zIndex: 2,
        }}
      >
        <HeraldicPanel size="md" style={{ padding: '20px 22px' }}>
          {/* Top-stripe — accent. */}
          <div
            style={{
              height: 2,
              marginBottom: 16,
              background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <ParchmentField label="Пол">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                }}
              >
                {(['male', 'female'] as const).map((g) => (
                  <PillButton
                    key={g}
                    active={draft.gender === g}
                    onClick={() => setDraft({ gender: g })}
                    accent={accent}
                  >
                    {g === 'male' ? '♂  Мужской' : '♀  Женский'}
                  </PillButton>
                ))}
              </div>
            </ParchmentField>

            <ParchmentField label="Телосложение">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                }}
              >
                {(['slim', 'medium', 'broad'] as const).map((b) => (
                  <PillButton
                    key={b}
                    active={draft.bodyType === b}
                    onClick={() => setDraft({ bodyType: b })}
                    accent={accent}
                    small
                  >
                    {b === 'slim' ? 'Стройное' : b === 'medium' ? 'Среднее' : 'Крепкое'}
                  </PillButton>
                ))}
              </div>
            </ParchmentField>

            {/* R232q20+: WotLK 3.3.5a-style variant pickers. Indexes persist
                to draft (and onward to the player record) — visual mesh
                swap is a TODO once CharSections.dbc + race-specific sections
                are wired in. */}
            <VariantStepper
              label="Кожа"
              accent={accent}
              count={counts.skin}
              value={draft.skinVariant}
              onChange={(v) => setDraft({ skinVariant: v })}
            />
            <VariantStepper
              label="Лицо"
              accent={accent}
              count={counts.face}
              value={draft.faceStyle}
              onChange={(v) => setDraft({ faceStyle: v })}
            />
            <VariantStepper
              label="Причёска"
              accent={accent}
              count={counts.hair}
              value={draft.hairStyle}
              onChange={(v) => setDraft({ hairStyle: v })}
            />
            <VariantStepper
              label="Цвет волос"
              accent={accent}
              count={counts.hairColour}
              value={draft.hairColourIdx}
              onChange={(v) => setDraft({ hairColourIdx: v })}
            />
            {counts.facialHair > 0 ? (
              <VariantStepper
                label={race.id === 'tauren' ? 'Рога' : 'Растительность'}
                accent={accent}
                count={counts.facialHair}
                value={draft.facialHair}
                onChange={(v) => setDraft({ facialHair: v })}
              />
            ) : (
              <div
                style={{
                  fontFamily: FONT.body,
                  fontStyle: 'italic',
                  fontSize: 11,
                  color: COLOR.textDim,
                  opacity: 0.7,
                }}
              >
                {/* Vanilla 3.3.5a: female rigs have no facial-hair axis. */}
                Растительность недоступна для женского облика {race.name.ru}.
              </div>
            )}

            <div
              style={{
                fontFamily: FONT.body,
                fontStyle: 'italic',
                fontSize: 11,
                color: COLOR.textDim,
                lineHeight: 1.45,
                paddingTop: 6,
                borderTop: `1px solid ${COLOR.goldDeep}33`,
                opacity: 0.75,
              }}
            >
              {/* TODO(rig): runtime mesh/texture swap when modular sections
                  land. Сейчас выбор сохраняется в карточку героя, а в игре
                  отрисуется как только подвезём модульные риги. */}
              Имя и подтверждение — ниже, на пергаменте Книги Зала.
            </div>
          </div>
        </HeraldicPanel>
      </div>
    </div>
  );
}

/**
 * R232q20+: « ◂  N / TOTAL  ▸ » stepper for WotLK 3.3.5a-style customization
 * variants (skin, face, hair, hair colour, facial hair). One-line, gold
 * pillish, matches the parchment aesthetic.
 */
function VariantStepper({
  label,
  accent,
  count,
  value,
  onChange,
}: {
  label: string;
  accent: string;
  count: number;
  value: number;
  onChange: (v: number) => void;
}) {
  const safeValue = count > 0 ? Math.max(0, Math.min(count - 1, value | 0)) : 0;
  const dec = () => onChange((safeValue - 1 + count) % count);
  const inc = () => onChange((safeValue + 1) % count);
  return (
    <ParchmentField label={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={dec}
          style={{
            width: 34,
            height: 34,
            background: `linear-gradient(180deg, rgba(40,28,16,0.6) 0%, rgba(11,9,7,0.85) 100%)`,
            border: `1px solid ${COLOR.goldDark}`,
            borderRadius: 2,
            color: COLOR.gold,
            fontFamily: FONT.display,
            fontSize: 16,
            cursor: 'pointer',
            transition: 'border-color 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLOR.goldDark; }}
        >
          ◂
        </button>
        <div
          style={{
            flex: 1,
            textAlign: 'center',
            fontFamily: FONT.mono,
            fontSize: 14,
            color: COLOR.goldHi,
            letterSpacing: '0.18em',
            background: 'rgba(0,0,0,0.35)',
            border: `1px solid ${COLOR.goldDeep}`,
            borderRadius: 2,
            padding: '8px 0',
          }}
        >
          {safeValue + 1} / {count}
        </div>
        <button
          type="button"
          onClick={inc}
          style={{
            width: 34,
            height: 34,
            background: `linear-gradient(180deg, rgba(40,28,16,0.6) 0%, rgba(11,9,7,0.85) 100%)`,
            border: `1px solid ${COLOR.goldDark}`,
            borderRadius: 2,
            color: COLOR.gold,
            fontFamily: FONT.display,
            fontSize: 16,
            cursor: 'pointer',
            transition: 'border-color 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLOR.goldDark; }}
        >
          ▸
        </button>
      </div>
    </ParchmentField>
  );
}

function ParchmentField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: FONT.caps,
          fontWeight: 500,
          fontSize: 11,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: COLOR.gold,
          paddingBottom: 8,
          marginBottom: 10,
          borderBottom: `1px solid ${COLOR.goldDeep}`,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function PillButton({
  active,
  onClick,
  accent,
  small,
  children,
}: {
  active: boolean;
  onClick: () => void;
  accent: string;
  small?: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -1, transition: { duration: 0.15 } }}
      whileTap={{ y: 0, transition: { duration: 0.08 } }}
      style={{
        padding: small ? '8px 8px' : '10px 12px',
        fontFamily: FONT.caps,
        fontSize: small ? 10 : 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: active ? COLOR.ink : COLOR.gold,
        background: active
          ? `linear-gradient(180deg, ${accent} 0%, ${COLOR.goldDark} 100%)`
          : 'linear-gradient(180deg, rgba(40,28,16,0.6) 0%, rgba(11,9,7,0.85) 100%)',
        border: `1px solid ${active ? accent : COLOR.goldDark}`,
        borderRadius: 2,
        cursor: 'pointer',
        boxShadow: active
          ? `0 0 12px ${accent}66, 0 1px 0 rgba(255,255,255,0.3) inset`
          : 'inset 0 0 6px rgba(0,0,0,0.5)',
        fontWeight: active ? 600 : 500,
        transition: 'border-color 200ms ease',
      }}
    >
      {children}
    </motion.button>
  );
}

// R232q5b: Swatch + NumberOption удалены — использовались только для
// fake skin/hair/hairStyle picker'ов в Customize.

// ─── STAGE 5: NAME + FINAL ────────────────────────────────────────────
function NameStage({
  draft,
  setDraft,
  race,
  cls,
  brandedFaction,
  canConfirm,
  onSubmit,
}: {
  draft: ReturnType<typeof useUI.getState>['draft'];
  setDraft: ReturnType<typeof useUI.getState>['setDraft'];
  race: RaceDef;
  cls: ClassDef;
  brandedFaction: BrandedFaction;
  canConfirm: boolean;
  onSubmit: () => void;
}) {
  const valid = isNameOk(draft.name);
  const hasInput = draft.name.trim().length > 0;
  const suggestions = brandedFaction.faction
    ? SUGGESTED_NAMES[brandedFaction.faction]
    : [];

  return (
    <div>
      <div className="text-center" style={{ marginBottom: 18 }}>
        <Eyebrow color={brandedFaction.accent}>Книга Зала · Подтверждение</Eyebrow>
        <div style={{ height: 8 }} />
        <DisplayHeading size={42}>Запиши имя в книгу</DisplayHeading>
        <div style={{ height: 12 }} />
        <BodyItalic color={COLOR.textDim} size={15}>
          Имя — это первое, что услышит мир. Запиши его в Книгу Зала, и
          летописец откроет тебе врата Стормвейла.
        </BodyItalic>
      </div>

      <FiligreeDivider icon="✒" label="Книга Зала" />

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
          gap: 24,
          maxWidth: 1280,
          margin: '0 auto',
        }}
      >
        {/* LEFT — name input + suggestions */}
        <HeraldicPanel size="md" style={{ padding: '28px 28px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <ParchmentField label="Имя персонажа">
              <input
                type="text"
                value={draft.name}
                onChange={(e) =>
                  setDraft({ name: e.target.value.slice(0, 20) })
                }
                placeholder="Введи имя"
                autoFocus
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: 22,
                  fontFamily: FONT.display,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  color: COLOR.goldHi,
                  background: `linear-gradient(180deg, ${COLOR.bgSoft} 0%, ${COLOR.bg} 100%)`,
                  border: `1.5px solid ${
                    hasInput
                      ? valid
                        ? COLOR.gold
                        : COLOR.horde
                      : COLOR.goldDark
                  }`,
                  borderRadius: 2,
                  outline: 'none',
                  boxShadow: 'inset 0 0 12px rgba(0,0,0,0.7)',
                  transition: 'border-color 200ms ease',
                }}
              />
              <div
                style={{
                  marginTop: 8,
                  fontFamily: FONT.mono,
                  fontSize: 11,
                  color: hasInput
                    ? valid
                      ? COLOR.gold
                      : COLOR.horde
                    : COLOR.goldDark,
                  letterSpacing: '0.04em',
                }}
              >
                {!hasInput
                  ? '3–20 символов · кириллица или латиница · без цифр и спецсимволов'
                  : valid
                  ? '✓ Имя достойно Книги'
                  : '✕ Запрещённые символы или слишком короткое имя'}
              </div>
            </ParchmentField>

            {suggestions.length > 0 && (
              <ParchmentField label="Подсказки летописца">
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setDraft({ name: s.slice(0, 20) })}
                      style={{
                        padding: '8px 14px',
                        fontFamily: FONT.body,
                        fontStyle: 'italic',
                        fontSize: 14,
                        color: COLOR.parchment,
                        background: 'rgba(0,0,0,0.35)',
                        border: `1px solid ${COLOR.goldDeep}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLOR.gold;
                        e.currentTarget.style.color = COLOR.goldHi;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLOR.goldDeep;
                        e.currentTarget.style.color = COLOR.parchment;
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </ParchmentField>
            )}

            <div
              style={{
                paddingTop: 6,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <GoldButton size="lg" onClick={onSubmit} disabled={!canConfirm}>
                ⚜ Создать героя ⚜
              </GoldButton>
            </div>
          </div>
        </HeraldicPanel>

        {/* RIGHT — final summary card */}
        <HeraldicPanel size="md" style={{ padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              height: 3,
              background: `linear-gradient(90deg, transparent, ${brandedFaction.accent}, transparent)`,
            }}
          />
          <div style={{ padding: '20px 24px 24px' }}>
            <Eyebrow color={brandedFaction.accent}>Итог</Eyebrow>
            <div style={{ height: 4 }} />
            <h3
              style={{
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: 22,
                color: COLOR.goldHi,
                margin: 0,
                letterSpacing: '0.03em',
              }}
            >
              {draft.name.trim() || 'Безымянный'}
            </h3>
            <div
              style={{
                fontFamily: FONT.caps,
                fontSize: 11,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: COLOR.gold,
                marginTop: 4,
              }}
            >
              {race.name.ru} · {cls.name.ru}
            </div>

            <FiligreeDivider icon="·" />

            <SummaryRow label="Сторона" value={brandedFaction.name} accent={brandedFaction.accent} />
            <SummaryRow label="Народ" value={race.name.ru} accent={race.colour} />
            <SummaryRow label="Столица" value={race.capital.ru} />
            <SummaryRow label="Стезя" value={cls.name.ru} accent={cls.colour} />
            <SummaryRow
              label="Ресурс"
              value={
                cls.resource === 'mana'
                  ? 'Мана'
                  : cls.resource === 'rage'
                  ? 'Ярость'
                  : cls.resource === 'focus'
                  ? 'Сосредоточение'
                  : 'Вера'
              }
            />
            <SummaryRow
              label="Пол"
              value={draft.gender === 'male' ? 'Мужской' : 'Женский'}
            />
            <SummaryRow
              label="Стать"
              value={
                draft.bodyType === 'slim'
                  ? 'Стройное'
                  : draft.bodyType === 'medium'
                  ? 'Среднее'
                  : 'Крепкое'
              }
            />
            {/* R232q5b: skin/hair/hair-style summary rows удалены —
                рендерили данные с фейковых слайдеров. Восстановить
                когда rig поддержит runtime colour swap. */}
          </div>
        </HeraldicPanel>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  accent?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: `1px solid ${COLOR.goldDeep}33`,
        gap: 12,
      }}
    >
      <span
        style={{
          fontFamily: FONT.caps,
          fontSize: 10,
          letterSpacing: '0.4em',
          textTransform: 'uppercase',
          color: COLOR.goldDark,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: FONT.body,
          fontSize: 14,
          color: accent ?? COLOR.parchment,
          fontWeight: 500,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// R232q5b: ColorChip удалён — рендерил фейковые skin/hair цвета в
// финальном summary.
